import { getServices } from '~/db/services';

import { dataExportImportCommon } from '~/lib/data/exportImportCommon';

export type UseCategoryTrialContent = {
  getTrialContentIdsForType: (type: TrialTypes) => string[];
  getTrialType: (type: string) => string;
  isTrialAvailableForTypeAndContentId: (
    content: IContentSchema,
    autoSave?: boolean
  ) => boolean;
  isTrialContent: (content: IContentSchema) => boolean;
  isATrialType: (type: string) => boolean;
  progressions: Ref<ProgressionSchema[]>;
  load: () => Promise<void>;
  saveTrialProgression: (
    type: TrialTypes | string,
    contentId: string
  ) => Promise<void>;
};

export const useCategoryTrialContent = (): UseCategoryTrialContent => {
  const { $db } = useNuxtApp();

  const profile = useProfileStore();

  const progressions = ref<ProgressionSchema[]>([]);

  const { exportProgressions } = dataExportImportCommon(
    $db,
    useApiFetch,
    profile.syncContext
  );

  const context = useContextStore();

  const _saveTrialProgression = async (type: string, contentId: string) => {
    if (profile.isASubscriber) return;
    await progressionsService.put({
      member: context.memberId,
      type: `trialContent_${type}`,
      contentId,
      local: 1,
      userUpdatedAt: new Date().toISOString(),
    } as ProgressionSchema);
    exportProgressions();
  };

  const config = useConfigStore();

  const { progressionsService } = getServices($db);

  const { progressions: progressionsDataSync } = useProgressionsTrialSyncGet();

  const getTrialContentIdsForType = (type: string) => {
    if (profile.isASubscriber) return;
    return progressions.value
      .filter((pr) => pr.type === `trialContent_${type}`)
      .map((pr) => pr.contentId);
  };

  const isATrialType = (type: string) => {
    const lockedContentTypes = Object.keys(profile.lockedContent);
    return lockedContentTypes.includes(type);
  };

  const getTrialType = (type: string) => {
    if (!isATrialType(type)) return null;
    return type;
  };

  const isTrialContent = (content: IContentSchema) => {
    const type = toValue(content).lockedContent || toValue(content).contentType;
    const contentId = toValue(content).id;
    const trialIds = getTrialContentIdsForType(type);
    if (trialIds.includes(contentId)) return true;
    return false;
  };

  const isTrialAvailableForTypeAndContentId = (
    content: IContentSchema,
    autoSave: boolean = false
  ) => {
    if (profile.isASubscriber) return true;

    const type = toValue(content).lockedContent || toValue(content).contentType;
    const contentId = toValue(content).id;

    const trialIds = getTrialContentIdsForType(type);
    if (trialIds.includes(contentId)) return true;

    if (trialIds.length < config.trialContentCount) {
      if (autoSave) _saveTrialProgression(type, contentId);
      return true;
    }
    return false;
  };

  const saveTrialProgression = async (type: string, contentId: string) => {
    await _saveTrialProgression(type, contentId);
  };

  watch(
    () => progressionsDataSync.value,
    (prs) => {
      progressions.value = prs;
    }
  );

  const load = async () => {
    if (profile.isASubscriber) return;
    progressions.value = await progressionsService.findTrialContents();
  };

  return {
    getTrialType,
    getTrialContentIdsForType,
    isTrialAvailableForTypeAndContentId,
    isTrialContent,
    isATrialType,
    progressions,
    saveTrialProgression,
    load,
  };
};
